import {Link, useLocation, useParams, useSearchParams} from 'react-router-dom';
import {useState, type ReactNode} from 'react';
import {formatDate, formatSsn, maskSsn} from '@common/lib/utils';
import {STALE_TIME_5_MIN} from '@common/constants/time';
import {IN_HOUSE_ADMINISTRATOR} from '@common/constants/administrators';
import {CODE_PERMISSIONS} from '@common/permissions';
import {useHasPermission, usePortalActionAllowed} from '@common-web/hooks/use-permissions';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {z} from 'zod';
import {
  User,
  MapPin,
  AlertCircle,
  Lock,
  Loader2,
  Pencil,
  Eye,
  EyeOff,
  X,
  Save,
  Ban,
  UserCheck,
  CalendarRange,
  ArrowLeft,
} from 'lucide-react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {Badge} from '@common-web/components/ui/badge';
import {Button} from '@common-web/components/ui/button';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@common-web/components/ui/tabs';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {trpc} from '@common-web/lib/trpc';
import {Label} from '@common-web/components/ui/label';
import {Input} from '@common-web/components/ui/input';
import {useToast} from '@common-web/hooks/use-toast';
import {useAuthStore} from '@common-web/stores/authStore';
import {useIsInternal} from '@common-web/providers/portalProvider';
import {ReadOnlyField} from '@client/components/InfoFields';
import {AppUserCard} from '@client/pages/client/AppUserCard';
import {useHasAppUserAffiliation} from '@client/hooks/useHasAppUserAffiliation';
import {MemberInvoicingTab} from '@internal/pages/invoices/MemberInvoicingTab';
import {EntityHistoryTab} from '@internal/components/audit/EntityHistoryTab';
import {TerminateMemberDialog} from '@client/pages/client/member-lifecycle/TerminateMemberDialog';
import {ReinstateMemberDialog} from '@client/pages/client/member-lifecycle/ReinstateMemberDialog';
import {MemberContextStrip} from '@client/pages/client/member-profile/MemberContextStrip';
import {
  MemberProductsTable,
  type MemberProductRow,
} from '@client/pages/client/member-profile/MemberProductsTable';
import {
  MemberDependentsSection,
  type MemberDependentRow,
} from '@client/pages/client/member-profile/MemberDependentsSection';
import {MemberNotesPanel} from '@client/pages/client/member-profile/MemberNotesPanel';
import {ChangeTierDialog} from '@client/pages/client/member-profile/ChangeTierDialog';
import {AddDependentDialog} from '@client/pages/client/member-profile/AddDependentDialog';
import {buildGroupMemberSearchPath} from '@common-web/hooks/useMemberSearchUrlState';
import {useMemberDetailReturnTo} from '@client/lib/memberDetailNavigation';
import {ROUTES} from '@client/lib/routes';
import {ProfileCard, ProfileRow} from '@common-web/components/profile-card';
import {
  CBS_ACTIVE_PILL_CLASS,
  CBS_CURRENT_PILL_CLASS,
  CBS_INACTIVE_PILL_CLASS,
} from '@common-web/components/account/cbsAccountsPresentation';

type MemberPlan = {
  planId: string;
  planDescription: string | null;
  planType: string | null;
  planStart: string | null;
  planEnd: string | null;
  tier: string | null;
  fee: number | null;
  groupId: string;
  divisionId: string;
};

type Member = {
  subscriberId: string;
  memberSeq: string;
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;
  nameSuffix: string | null;
  birthDate: string | null;
  ssn: string | null;
  alternateId: string | null;
  sex: string | null;
  relationship: string | null;
  relationshipLabel: string;
  coverageType: string | null;
  dateEnrolled: string | null;
  disenrollDate: string | null;
  createdDate: string | null;
  smoker: string | null;
  adultDependent: string | null;
  notes: string | null;
  isActive: boolean;
  plans: MemberPlan[];
};

type Subscriber = {
  subscriberId: string;
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;
  address: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
    county: string | null;
  };
  email: string | null;
  homePhone: string | null;
  workPhone: string | null;
  dateEnrolled: string | null;
  disenrollDate: string | null;
  status: string | null;
  notes: string | null;
  isActive: boolean;
};

// Accepts an empty string (leaving the field blank keeps the existing value);
// otherwise requires a real calendar date (YYYY-MM-DD) that is not in the future.
// The regex alone accepts impossible dates (e.g. 2026-02-31), so parse and
// round-trip the parts through a Date to reject overflowed month/day values.
const isEmptyOrValidPastDate = (value: string): boolean => {
  if (value === '') return true;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12) return false;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return false;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date <= today;
};

const subscriberFormSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(50, 'Max 50 characters'),
  lastName: z.string().trim().min(1, 'Last name is required').max(50, 'Max 50 characters'),
  email: z.string().trim().email('Invalid email address').max(550).or(z.literal('')),
  homePhone: z.string().trim().max(20, 'Max 20 characters'),
  workPhone: z.string().trim().max(20, 'Max 20 characters'),
  address: z.string().trim().max(500, 'Max 500 characters'),
  address2: z.string().trim().max(500, 'Max 500 characters'),
  city: z.string().trim().max(50, 'Max 50 characters'),
  state: z.string().trim().max(2, 'Max 2 characters'),
  zipCode: z.string().trim().max(50, 'Max 50 characters'),
  ssn: z
    .string()
    .trim()
    .refine((v) => v === '' || /^\d{9}$/.test(v.replace(/\D/g, '')), 'SSN must be 9 digits'),
  dateOfBirth: z.string().trim().refine(isEmptyOrValidPastDate, 'Enter a valid date'),
});

type SubscriberForm = z.infer<typeof subscriberFormSchema>;

const TABS = ['overview', 'history', 'invoicing'] as const;
type DetailTab = (typeof TABS)[number];

/**
 * History and Invoicing are internal-portal only and need a VBA subscriber id
 * (external-TPA members have none); anything else falls back to Overview.
 */
const parseTab = (value: string | null, extraTabsAvailable: boolean): DetailTab =>
  extraTabsAvailable && TABS.includes(value as DetailTab) ? (value as DetailTab) : 'overview';

/** Demo-parity horizontal label/value row: fixed-width quiet label beside the
 *  value, replacing the stacked `ReadOnlyField` layout on the internal portal. */
function computeAge(birthDateIso: string): number {
  const birth = new Date(birthDateIso);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  if (
    now.getMonth() < birth.getMonth() ||
    (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())
  ) {
    age -= 1;
  }
  return age;
}

const InfoRow = ({label, value}: {label: string; value: ReactNode}) => (
  <div className='flex items-start gap-3 py-1.5'>
    <span className='w-[104px] shrink-0 text-[12px] font-medium leading-snug text-muted-foreground'>
      {label}
    </span>
    {/* min-w-0 lets the value shrink inside the flex row; break-words stops
        long tokens (IDs, emails, dates) from spilling outside the card. */}
    <span className='min-w-0 flex-1 break-words text-[13px] leading-snug text-foreground'>
      {value}
    </span>
  </div>
);

/**
 * App-account dependents carry a birth date as `{year, month, day}` parts, where VBA
 * members carry an ISO string. Normalised here so one dependents row type serves both.
 */
function formatAppDependentBirthDate(
  birthDate: {year: number; month: number; day: number} | null | undefined,
): string | null {
  if (!birthDate?.year || !birthDate.month || !birthDate.day) return null;
  return new Date(Date.UTC(birthDate.year, birthDate.month - 1, birthDate.day)).toISOString();
}

export const GroupMemberDetails = () => {
  // One page, two routes: `member/:id` for a CBS member and
  // `member/external-tpa/:memberId` for an Alpha/Elysian one. The TPA feeds carry no
  // VBA subscriber, so the member is addressed by its EDI id instead — the endpoint
  // maps it onto the same response and the layout below is unchanged.
  const {id, memberId: externalTpaMemberId} = useParams<{id: string; memberId: string}>();
  const detailKey = id ?? externalTpaMemberId;
  const isInternal = useIsInternal();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get('tab'), isInternal && !!id);
  const returnTo = useMemberDetailReturnTo();

  const setTab = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'overview') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, {replace: true, state: location.state});
  };

  // Internal uses the flat sand-theme shell and cards (demo parity); client
  // portals keep the gradient shell + elevated white cards.
  const shellClass = isInternal
    ? 'min-h-screen'
    : 'min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5';
  const cardClass = isInternal
    ? 'rounded-xl border bg-card shadow-none'
    : 'border border-gray-200 shadow-sm';

  const {toast} = useToast();
  const utils = trpc.useUtils();
  const isGroupUser = useAuthStore((state) => state.isGroupUser());
  const hasClientProfile = useAuthStore((state) => !!state.user?.clientProfile);
  const canGroupAdmin = useHasPermission(CODE_PERMISSIONS.groups.adminAccess.key);
  const isClientAdmin = hasClientProfile && canGroupAdmin;
  // Full-SSN reveal requires `phi.view_member_ssn_full`, and the API never
  // ships full SSNs to group-scoped callers regardless of permissions (same
  // wire policy as members.search) — it pre-masks for both, so hiding the
  // toggle just avoids a no-op reveal.
  const canRevealSsn = useHasPermission(CODE_PERMISSIONS.phi.viewMemberSsnFull.key) && !isGroupUser;
  const hasEditMemberPermission = usePortalActionAllowed(CODE_PERMISSIONS.members.edit.key);
  const canEditCoverage = useHasPermission(CODE_PERMISSIONS.members.editCoverage.key);
  const canEditDependents = useHasPermission(CODE_PERMISSIONS.members.editDependents.key);
  const canTerminateMember = useHasPermission(CODE_PERMISSIONS.members.terminate.key);

  const [subscriberEditing, setSubscriberEditing] = useState(false);
  const [ssnRevealed, setSsnRevealed] = useState(false);
  const [externalIdEditing, setExternalIdEditing] = useState(false);
  const [externalIdValue, setExternalIdValue] = useState('');
  const [externalIdError, setExternalIdError] = useState<string | null>(null);
  const [lifecycleDialog, setLifecycleDialog] = useState<'terminate' | 'reinstate' | null>(null);

  const [addDependentOpen, setAddDependentOpen] = useState(false);
  const [tierProduct, setTierProduct] = useState<MemberProductRow | null>(null);
  const [removeDependent, setRemoveDependent] = useState<{
    memberSeq: string;
    name: string;
  } | null>(null);

  const closeLifecycleDialog = () => setLifecycleDialog(null);

  const subscriberForm = useForm<SubscriberForm>({
    resolver: zodResolver(subscriberFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      homePhone: '',
      workPhone: '',
      address: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
      ssn: '',
      dateOfBirth: '',
    },
  });

  const {data, isLoading, isFetching, error} = trpc.vba.members.getDetails.useQuery(
    externalTpaMemberId ? {externalTpaMemberId} : {subscriberId: id!},
    {enabled: !!detailKey},
  );

  // Empty unless this tenant has external TPAs, which is the only situation where the
  // badge says anything: a Quantum admin already knows their groups are Quantum's, so
  // naming the tenant on every row is noise. With TPAs in play the distinction is real
  // — this group is administered by CBS, that one by Elysian — so the badge earns its
  // place and names the administrator rather than the tenant.
  const externalTpasQuery = trpc.vba.members.listExternalTpas.useQuery(undefined, {
    staleTime: STALE_TIME_5_MIN,
  });
  const showAdministratorBadge = (externalTpasQuery.data?.length ?? 0) > 0;

  // Set only for an external-TPA member; drives the read-only banner and gates the
  // edit affordances on data the TPA owns.
  const externalTpaLabel = data?.externalTpaLabel ?? null;
  const isExternalTpaMember = !!data?.externalTpa;

  // The TPA owns the member record — it arrives on their daily file, so a write here
  // would be overwritten by the next one. The app account is ours and stays editable;
  // only member-data affordances are gated.
  const canEditMember = hasEditMemberPermission && !isExternalTpaMember;

  const hasAppUserAffiliation = useHasAppUserAffiliation(data?.enrollments);

  // Same query key as AppUserCard's, so React Query serves both from one fetch.
  // Used only to derive the "App User" tag chip in the context strip.
  const appUserQuery = trpc.vba.appUser.getByMember.useQuery(
    {subscriberId: id!},
    {enabled: !!id && !externalTpaMemberId && hasAppUserAffiliation},
  );
  const tpaAppUserQuery = trpc.vba.appUser.getByExternalTpaMember.useQuery(
    {memberId: externalTpaMemberId!},
    {enabled: !!externalTpaMemberId},
  );

  // SSN and DOB are member-level VBA writes, but they're saved together with the
  // subscriber's core fields as part of the single "Edit" flow (orchestrated in
  // handleSaveSubscriber), so these mutations carry no standalone side effects.
  // updateMemberIdentity applies SSN + DOB in one VBA write so a member missing
  // both can be filled in (the single-field endpoints require the other to exist).
  const updateSubscriberMutation = trpc.vba.members.updateSubscriber.useMutation();
  const updateIdentityMutation = trpc.vba.members.updateMemberIdentity.useMutation();

  const updateExternalIdMutation = trpc.vba.members.updateMemberExternalId.useMutation({
    onSuccess: () => {
      toast({title: 'External ID Updated', description: 'Member external ID has been saved.'});
      setExternalIdEditing(false);
      setExternalIdError(null);
      utils.vba.members.getDetails.invalidate({subscriberId: id!});
    },
    onError: (err) => {
      toast({
        title: 'Error',
        description: err.message || 'Failed to update external ID',
        variant: 'destructive',
      });
    },
  });

  const isSavingSubscriber = updateSubscriberMutation.isPending || updateIdentityMutation.isPending;

  const startEditingSubscriber = (sub: Subscriber) => {
    const primary = data?.members?.[0];
    subscriberForm.reset({
      firstName: sub.firstName ?? '',
      lastName: sub.lastName ?? '',
      email: sub.email ?? '',
      homePhone: sub.homePhone ?? '',
      workPhone: sub.workPhone ?? '',
      address: sub.address.line1 ?? '',
      address2: sub.address.line2 ?? '',
      city: sub.address.city ?? '',
      state: sub.address.state ?? '',
      zipCode: sub.address.zipCode ?? '',
      // Never pre-fill the SSN — leaving it empty preserves the existing value.
      ssn: '',
      dateOfBirth: primary?.birthDate ? primary.birthDate.slice(0, 10) : '',
    });
    setSubscriberEditing(true);
  };

  // Saves the subscriber's core fields plus the primary member's SSN/DOB in one
  // flow. SSN and DOB are separate member-level VBA writes, so they only fire when
  // actually changed; all three run under a single toast/close/invalidate.
  const handleSaveSubscriber = async (formData: SubscriberForm) => {
    const primary = data?.members?.[0];
    if (!data?.subscriber || !primary) return;

    try {
      await updateSubscriberMutation.mutateAsync({
        subscriberId: data.subscriber.subscriberId,
        data: {
          firstName: formData.firstName || undefined,
          lastName: formData.lastName || undefined,
          email: formData.email || null,
          homePhone: formData.homePhone || null,
          workPhone: formData.workPhone || null,
          address: formData.address || null,
          address2: formData.address2 || null,
          city: formData.city || null,
          state: formData.state || null,
          zipCode: formData.zipCode || null,
        },
      });

      // SSN + DOB are a single member-level VBA write; only send the fields that
      // actually changed so a member missing one can be filled in without the other.
      const newSsnDigits = formData.ssn.replace(/\D/g, '');
      const currentSsnDigits = (primary.ssn ?? '').replace(/\D/g, '');
      const ssnChanged = !!newSsnDigits && newSsnDigits !== currentSsnDigits;

      const currentDob = primary.birthDate ? primary.birthDate.slice(0, 10) : '';
      const dobChanged = !!formData.dateOfBirth && formData.dateOfBirth !== currentDob;

      if (ssnChanged || dobChanged) {
        await updateIdentityMutation.mutateAsync({
          subscriberId: primary.subscriberId,
          memberSeq: primary.memberSeq,
          ...(ssnChanged ? {ssn: newSsnDigits} : {}),
          ...(dobChanged ? {dateOfBirth: formData.dateOfBirth} : {}),
        });
      }

      toast({title: 'Subscriber Updated', description: 'Subscriber information has been saved.'});
      setSubscriberEditing(false);
      utils.vba.members.getDetails.invalidate({subscriberId: id!});
      utils.vba.members.search.invalidate();
    } catch (err) {
      // The subscriber and identity (SSN/DOB) writes are separate backend calls, so a
      // failure in the second leaves the first committed. Refetch so the UI reflects
      // whatever partially persisted, and surface that the update may be partial.
      utils.vba.members.getDetails.invalidate({subscriberId: id!});
      toast({
        title: 'Error',
        description:
          err instanceof Error
            ? `${err.message} Some changes may not have been saved.`
            : 'Failed to update subscriber. Some changes may not have been saved.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className={shellClass}>
        <div className='flex flex-col gap-0'>
          <PageHeader
            title='Member details'
            description='Loading member information…'
            icon={<User className='h-6 w-6' />}
            isLoading
          />

          {/* Content Skeleton */}
          <div className='px-8 py-6 max-w-7xl mx-auto w-full'>
            {/* Tabs Skeleton */}
            <div className='flex gap-2 mb-6'>
              <Skeleton className='h-10 w-36' />
              <Skeleton className='h-10 w-36' />
              <Skeleton className='h-10 w-44' />
            </div>

            {/* Card Skeleton */}
            <Card
              className={
                isInternal
                  ? `${cardClass} overflow-hidden`
                  : 'border-0 shadow-lg bg-white overflow-hidden'
              }
            >
              <CardHeader
                className={
                  isInternal
                    ? 'border-b px-6 py-4'
                    : 'bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'
                }
              >
                <Skeleton className='h-6 w-48' />
              </CardHeader>
              <CardContent className='p-6 space-y-6'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  {[...Array(4)].map((_, i) => (
                    <div key={i}>
                      <Skeleton className='h-3 w-24 mb-2' />
                      <Skeleton className='h-5 w-40' />
                    </div>
                  ))}
                </div>
                <div className='pt-6 border-t'>
                  <Skeleton className='h-5 w-40 mb-4' />
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    {[...Array(3)].map((_, i) => (
                      <div key={i}>
                        <Skeleton className='h-3 w-20 mb-2' />
                        <Skeleton className='h-5 w-36' />
                      </div>
                    ))}
                  </div>
                </div>
                <div className='pt-6 border-t'>
                  <Skeleton className='h-5 w-24 mb-4' />
                  <div className='ml-6 space-y-2'>
                    <Skeleton className='h-4 w-48' />
                    <Skeleton className='h-4 w-40' />
                    <Skeleton className='h-4 w-44' />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className={shellClass}>
        <div className='px-8 py-6 max-w-7xl mx-auto'>
          <Alert variant='destructive' className='border-red-200 bg-red-50'>
            <AlertCircle className='h-4 w-4' />
            <AlertDescription>{error?.message || 'Member not found'}</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (!data || !data.members || data.members.length === 0) {
    return null;
  }

  const primaryMember = data.members[0];
  const subscriber = data.subscriber;
  // Null for an external-TPA member, which disables Terminate/Reinstate and their
  // dialogs. Their `groupId` is a TPA group, not a VBA one, so a lifecycle write would
  // be aimed at a group VBA has never heard of — and terminating is a member-data edit,
  // which the TPA owns.
  const lifecycleGroupId = isExternalTpaMember ? null : (data.enrollments?.[0]?.groupId ?? null);
  const composedMemberId = `${primaryMember.subscriberId}-${primaryMember.memberSeq}`;
  const memberFullName = `${primaryMember.firstName} ${primaryMember.lastName}`;
  const refetchDetails = () => utils.vba.members.getDetails.invalidate({subscriberId: id!});

  const productRows: MemberProductRow[] = data.members.flatMap((member) =>
    (member.plans ?? []).map((plan, idx) => ({
      id: `${member.memberSeq}-${plan.planId}-${idx}`,
      name: plan.planDescription || plan.planId,
      category: plan.planType ?? null,
      fee: plan.fee ?? null,
      status: plan.planEnd ? ('Inactive' as const) : ('Active' as const),
      activeDate: plan.planStart ?? null,
      inactiveDate: plan.planEnd ?? null,
      planId: plan.planId,
      memberSeq: member.memberSeq,
      memberName: `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim(),
    })),
  );

  // Product/dependent writes go straight to VBA. Internal portal gates them on
  // the coverage/dependents permissions; client portals keep the existing
  // internal-only bar (these writes are not exposed there).
  const canManageCoverage = isInternal && canEditCoverage && !!lifecycleGroupId;
  const canManageDependents =
    isInternal && canEditDependents && !!lifecycleGroupId && !isExternalTpaMember;

  // A CBS member's family comes from the VBA roster — the subscriber's other
  // `Member_Seq` rows. An external-TPA member has none: those feeds carry primaries
  // only, and the household exists solely on the app account. Both render through the
  // same section so the page reads identically; the TPA rows leave the columns their
  // source doesn't carry (enrollment date, plan coverage) blank rather than inventing
  // them.
  const dependentRows: MemberDependentRow[] = isExternalTpaMember
    ? (tpaAppUserQuery.data?.dependents ?? [])
        .filter((d) => d.relationship !== 'primary')
        .map((d) => ({
          memberSeq: d.id,
          firstName: d.userDetails.firstName,
          lastName: d.userDetails.lastName,
          relationshipLabel: d.relationship,
          birthDate: formatAppDependentBirthDate(d.userDetails.birthDate),
          isActive: !d.isDeactivated,
          effectiveDate: null,
          hasCoverage: undefined,
        }))
    : data.members
        .filter((m) => m.memberSeq !== primaryMember.memberSeq)
        .map((m) => ({
          memberSeq: m.memberSeq,
          firstName: m.firstName,
          lastName: m.lastName,
          relationshipLabel: m.relationshipLabel,
          birthDate: m.birthDate ?? null,
          isActive: m.isActive,
          effectiveDate: m.dateEnrolled ?? null,
          hasCoverage: (m.plans ?? []).some((p) => !p.planEnd),
        }));

  // Distinct active products on the subscriber, used to seed a new dependent's
  // coverage.
  const activeSubscriberProducts = Array.from(
    new Map(
      productRows
        .filter((p) => p.status === 'Active' && p.memberSeq === primaryMember.memberSeq)
        .map((p) => [p.planId, {productId: p.planId, name: p.name}]),
    ).values(),
  );

  // The coverage tier shown in the context strip comes from the primary's
  // active plan; that same plan is what a tier change writes against.
  const tierPlan = primaryMember.plans.find((p) => !p.planEnd) ?? null;

  // At-a-glance tags for the context strip. Dependent covers the rare subscriber whose
  // displayed member row isn't the '01' primary (relationship code '01' = Primary).
  //
  // No source-system tag: this page serves CBS members out of VBA and Alpha/Elysian
  // members out of their EDI feed, so a blanket "VBA" was wrong for the latter and
  // told the former nothing they needed.
  // Client portals only: gap coverage is a member-facing coverage question, and the
  // internal portal reads VBA's own enrollment dates.
  const showGapCoverage = !isInternal && !!data?.isInGapCoverage;

  const memberTags = [
    appUserQuery.data?.appUser ? 'App User' : null,
    primaryMember.relationship && primaryMember.relationship !== '01' ? 'Dependent' : null,
    // CBS's 35-day run-out after the enrollment ends. A tag rather than a status,
    // because the member reads as Termed — their enrollment HAS ended — and this is
    // the coverage that follows it.
    showGapCoverage ? 'Gap Coverage' : null,
  ].filter((tag): tag is string => tag !== null);

  // Coverage windows — periods of enrollment from VBA (current + past). Each
  // window lists the family's plans whose active range overlaps the window.
  type CoverageWindow = {
    key: string;
    startDate: string | null;
    endDate: string | null;
    productNames: string[];
  };
  const coverageWindows: CoverageWindow[] = (data.enrollments ?? [])
    .map((enrollment, idx): CoverageWindow => {
      const startDate = enrollment.startDate ?? null;
      const endDate = enrollment.termDate ?? enrollment.endDate ?? null;
      // ISO timestamps compare correctly as strings; a missing plan bound is
      // treated as open-ended on that side.
      const overlapsWindow = (planStart: string | null, planEnd: string | null) => {
        if (endDate && planStart && planStart > endDate) return false;
        if (startDate && planEnd && planEnd < startDate) return false;
        return true;
      };
      const productNames = Array.from(
        new Set(
          data.members.flatMap((member) =>
            (member.plans ?? [])
              .filter((p) => overlapsWindow(p.planStart ?? null, p.planEnd ?? null))
              .map((p) => p.planDescription || p.planId),
          ),
        ),
      );
      return {
        key: `${enrollment.groupId}-${startDate ?? idx}`,
        startDate,
        endDate,
        productNames,
      };
    })
    .sort((a, b) => {
      if (a.endDate === null && b.endDate !== null) return -1;
      if (b.endDate === null && a.endDate !== null) return 1;
      return (b.startDate ?? '').localeCompare(a.startDate ?? '');
    });
  const currentWindow = coverageWindows.find((w) => w.endDate === null) ?? null;
  const pastWindows = coverageWindows.filter((w) => w !== currentWindow);

  const renderCoverageWindow = (w: CoverageWindow, current: boolean) => (
    <div key={w.key} className='border-b border-gray-100 py-3 first:pt-0 last:border-b-0 last:pb-0'>
      <div className='flex items-center gap-2'>
        <span className='text-sm font-semibold text-brand-navy'>
          {formatDate(w.startDate, 'MMM D, YYYY', 'N/A')} –{' '}
          {w.endDate ? formatDate(w.endDate, 'MMM D, YYYY') : 'present'}
        </span>
        {current && (
          <Badge
            className={
              isInternal
                ? `border-0 px-1.5 py-0 text-xs font-semibold ${CBS_CURRENT_PILL_CLASS}`
                : 'bg-emerald text-white border-0 px-1.5 py-0 text-xs font-semibold'
            }
          >
            Current
          </Badge>
        )}
      </div>
      <p className='mt-0.5 text-xs text-brand-gray'>
        {w.productNames.length > 0 ? w.productNames.join(' · ') : 'No products on file'}
      </p>
    </div>
  );

  const startEditingExternalId = () => {
    setExternalIdValue(primaryMember.alternateId ?? '');
    setExternalIdError(null);
    setExternalIdEditing(true);
  };

  const handleSaveExternalId = () => {
    const trimmed = externalIdValue.trim();
    if (trimmed.length === 0) {
      setExternalIdError('External ID is required');
      return;
    }
    if (trimmed.length > 32) {
      setExternalIdError('External ID must be 32 characters or fewer');
      return;
    }
    updateExternalIdMutation.mutate({
      subscriberId: primaryMember.subscriberId,
      memberSeq: primaryMember.memberSeq,
      externalId: trimmed,
    });
  };

  // Internal uses the demo's soft status tones; client portals keep the solid
  // emerald / gray badges.
  const getStatusBadge = (isActive: boolean) => {
    if (isInternal) {
      return isActive ? (
        <Badge className={`border-0 px-3 py-1 font-semibold ${CBS_ACTIVE_PILL_CLASS}`}>
          Active
        </Badge>
      ) : (
        <Badge className={`border-0 px-3 py-1 font-semibold ${CBS_INACTIVE_PILL_CLASS}`}>
          Termed
        </Badge>
      );
    }
    return isActive ? (
      <Badge className='bg-emerald text-white border-0 font-semibold px-3 py-1'>Active</Badge>
    ) : (
      <Badge className='bg-brand-gray text-white border-0 font-semibold px-3 py-1'>Termed</Badge>
    );
  };

  // Rendered beside the Active/Termed badge wherever status is shown, never instead of
  // it: gap coverage is what follows a termination, not an alternative to one.
  const gapCoverageBadge = showGapCoverage ? (
    <Badge className='border-0 bg-sky-50 px-3 py-1 font-semibold text-sky-700'>
      Gap Coverage
      {data.gapCoverageEndDate
        ? ` · ends ${formatDate(data.gapCoverageEndDate, 'MM/DD/YYYY', '—')}`
        : ''}
    </Badge>
  ) : null;

  const formatPhone = (phone: string | null | undefined) => {
    if (!phone) return isInternal ? '—' : 'N/A';
    const cleaned = ('' + phone).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phone;
  };

  const backToMembersPath =
    returnTo ??
    (data.enrollments?.[0]?.groupId
      ? buildGroupMemberSearchPath(
          data.enrollments[0].groupId,
          data.enrollments[0].groupName ?? undefined,
        )
      : ROUTES.memberSearch.to());

  // Internal renders the demo's horizontal label/value rows with em-dash
  // fallbacks; client portals keep the stacked ReadOnlyField layout and 'N/A'.
  const dash = isInternal ? '—' : 'N/A';
  const renderField = (label: string, value: ReactNode) =>
    isInternal ? (
      <InfoRow key={label} label={label} value={value} />
    ) : (
      <ReadOnlyField key={label} label={label} value={value} />
    );

  return (
    <div className={shellClass}>
      <div className='flex flex-col gap-0'>
        {isInternal && (
          <div className='mx-auto w-full max-w-7xl px-8 pt-4'>
            <Link
              to={backToMembersPath}
              className='inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground'
            >
              <ArrowLeft className='h-4 w-4' />
              Back to Members
            </Link>
          </div>
        )}
        {isInternal ? (
          /* Demo-parity flat hero: content-first header on the page background —
             accent icon, bold title, quiet mono-ID subtitle with an inline status
             pill, right-aligned quiet actions, and a thin divider below. */
          <div className='mx-auto w-full max-w-7xl px-8 pt-4'>
            <div className='flex items-start justify-between gap-4'>
              <div className='flex min-w-0 items-center gap-2.5'>
                <User className='h-5 w-5 shrink-0 text-primary' />
                <div className='min-w-0'>
                  <h1 className='text-[22px] font-bold leading-tight text-foreground'>
                    {primaryMember.firstName} {primaryMember.lastName}
                  </h1>
                  <p className='mt-0.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-muted-foreground'>
                    <span>
                      Subscriber ID: <span className='font-mono'>{primaryMember.subscriberId}</span>
                    </span>
                    {primaryMember.isActive ? (
                      <Badge
                        className={`border-0 px-1.5 py-0.5 text-xs font-semibold ${CBS_ACTIVE_PILL_CLASS}`}
                      >
                        Active
                      </Badge>
                    ) : (
                      <Badge
                        className={`border-0 px-1.5 py-0.5 text-xs font-semibold ${CBS_INACTIVE_PILL_CLASS}`}
                      >
                        Termed
                      </Badge>
                    )}
                  </p>
                </div>
              </div>
              <div className='flex shrink-0 items-center gap-2'>
                {isFetching && !isLoading && (
                  <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
                )}
                {subscriber && canEditMember && (
                  <Button
                    variant='outline'
                    onClick={() => startEditingSubscriber(subscriber as Subscriber)}
                  >
                    <Pencil className='mr-2 h-4 w-4' />
                    Edit
                  </Button>
                )}
                {data?.enrollments?.[0]?.groupId && (
                  <>
                    {canTerminateMember &&
                      (primaryMember.isActive ? (
                        <Button variant='outline' onClick={() => setLifecycleDialog('terminate')}>
                          <Ban className='mr-2 h-4 w-4' />
                          Terminate
                        </Button>
                      ) : (
                        <Button onClick={() => setLifecycleDialog('reinstate')}>
                          <UserCheck className='mr-2 h-4 w-4' />
                          Reinstate
                        </Button>
                      ))}
                  </>
                )}
              </div>
            </div>
            <div className='mt-4 border-t' />
          </div>
        ) : (
          <PageHeader
            title={`${primaryMember.firstName} ${primaryMember.lastName}`}
            description={`Subscriber ID: ${primaryMember.subscriberId} • ${
              primaryMember.isActive ? 'Active' : 'Termed'
            }${
              showGapCoverage
                ? ` • Gap coverage${
                    data.gapCoverageEndDate
                      ? ` ends ${formatDate(data.gapCoverageEndDate, 'MM/DD/YYYY', '—')}`
                      : ''
                  }`
                : ''
            }`}
            icon={<User className='h-6 w-6' />}
            isFetching={isFetching}
            isLoading={isLoading}
          >
            {subscriber && canEditMember && (
              <PageHeaderAction
                tone='secondary'
                onClick={() => startEditingSubscriber(subscriber as Subscriber)}
              >
                <Pencil className='h-4 w-4 mr-2' />
                Edit
              </PageHeaderAction>
            )}
            <PageHeaderAction tone='secondary' asChild>
              <Link to={backToMembersPath}>Back to Members</Link>
            </PageHeaderAction>
          </PageHeader>
        )}

        {isExternalTpaMember && (
          <div className='px-8 pt-4 max-w-7xl mx-auto w-full'>
            {/* Scoped to member data on purpose. The app account below is ours to
                manage, and this wording stays correct when editing an app user is
                replaced by deactivate/delete. */}
            <Alert className='border-amber-200 bg-amber-50'>
              <Lock className='h-4 w-4 text-amber-700' />
              <AlertDescription className='text-amber-900'>
                This member&apos;s details are managed by {externalTpaLabel} and arrive on their
                daily eligibility file, so they can&apos;t be edited here.
              </AlertDescription>
            </Alert>
          </div>
        )}

        <div className='px-8 pt-4 max-w-7xl mx-auto w-full'>
          <MemberContextStrip
            groupId={data.enrollments?.[0]?.groupId ?? null}
            groupName={data.enrollments?.[0]?.groupName ?? null}
            tenantName={
              showAdministratorBadge ? (externalTpaLabel ?? IN_HOUSE_ADMINISTRATOR.label) : null
            }
            isActive={primaryMember.isActive}
            coverageTier={tierPlan?.tier ?? primaryMember.coverageType ?? null}
            effectiveDate={primaryMember.dateEnrolled ?? null}
            termDate={data.enrollments?.[0]?.termDate ?? primaryMember.disenrollDate ?? null}
            employeeId={primaryMember.alternateId ?? null}
            tags={memberTags}
            onChangeTier={
              canManageCoverage && tierPlan
                ? () =>
                    setTierProduct(
                      productRows.find(
                        (p) =>
                          p.planId === tierPlan.planId &&
                          p.memberSeq === primaryMember.memberSeq &&
                          p.status === 'Active',
                      ) ?? null,
                    )
                : undefined
            }
          />
        </div>

        {/* Main Content Area */}
        <div className='px-8 py-6 max-w-7xl mx-auto w-full'>
          <Tabs value={tab} onValueChange={setTab} noHash className='space-y-6'>
            {/* Internal: demo's underline strip (Invoicing tucked right, smaller);
                client portals keep the pill-style list. */}
            <TabsList
              className={
                isInternal
                  ? 'flex h-auto w-full justify-start gap-1 rounded-none border-b bg-transparent p-0'
                  : 'bg-gray-100 p-1'
              }
            >
              <TabsTrigger
                value='overview'
                noHash
                className={
                  isInternal
                    ? '-mb-px rounded-none border-b-2 border-transparent px-4 py-2.5 text-[13px] font-medium text-muted-foreground data-[state=active]:border-[#4a79bc] data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none'
                    : 'data-[state=active]:bg-white data-[state=active]:text-brand-navy data-[state=active]:shadow-sm'
                }
              >
                {!isInternal && <User className='h-4 w-4 mr-2' />}
                Overview
              </TabsTrigger>
              {isInternal && (
                <TabsTrigger
                  value='history'
                  noHash
                  className='-mb-px rounded-none border-b-2 border-transparent px-4 py-2.5 text-[13px] font-medium text-muted-foreground data-[state=active]:border-[#4a79bc] data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none'
                >
                  History
                </TabsTrigger>
              )}
              {isInternal && (
                <TabsTrigger
                  value='invoicing'
                  noHash
                  className='-mb-px ml-auto rounded-none border-b-2 border-transparent px-3 py-2.5 text-[12px] font-medium text-muted-foreground data-[state=active]:border-[#4a79bc] data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none'
                >
                  Invoicing
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value='overview' className='space-y-6'>
              <div
                className={
                  isInternal
                    ? 'grid grid-cols-1 items-start gap-5 lg:grid-cols-2'
                    : 'grid grid-cols-1 gap-6 md:grid-cols-3'
                }
              >
                {isInternal ? (
                  /* Demo's 2x2 Identity · Contact · Employment · Reference split. */
                  <>
                    <ProfileCard title='Identity'>
                      <ProfileRow label='Subscriber ID' value={primaryMember.subscriberId} mono />
                      <ProfileRow
                        label='Name'
                        value={[
                          primaryMember.firstName,
                          primaryMember.middleName
                            ? `${primaryMember.middleName.charAt(0)}.`
                            : null,
                          primaryMember.lastName,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      />
                      <ProfileRow
                        label='Date of Birth'
                        value={
                          primaryMember.birthDate
                            ? formatDate(primaryMember.birthDate, 'MMM D, YYYY')
                            : null
                        }
                      />
                      <ProfileRow
                        label='Age'
                        value={
                          primaryMember.birthDate
                            ? String(computeAge(primaryMember.birthDate))
                            : null
                        }
                      />
                      <ProfileRow
                        label='Gender'
                        value={
                          primaryMember.sex === 'M'
                            ? 'Male'
                            : primaryMember.sex === 'F'
                              ? 'Female'
                              : primaryMember.sex
                        }
                      />
                      <ProfileRow
                        label='SSN'
                        value={
                          <span className='flex items-center gap-2'>
                            {ssnRevealed
                              ? formatSsn(primaryMember.ssn)
                              : maskSsn(primaryMember.ssn)}
                            {primaryMember.ssn && (
                              <button
                                type='button'
                                className='rounded p-0.5 text-primary hover:bg-primary/10'
                                onClick={() => setSsnRevealed((v) => !v)}
                                aria-label={ssnRevealed ? 'Hide SSN' : 'Show SSN'}
                              >
                                {ssnRevealed ? (
                                  <EyeOff className='h-3.5 w-3.5' />
                                ) : (
                                  <Eye className='h-3.5 w-3.5' />
                                )}
                              </button>
                            )}
                          </span>
                        }
                      />
                    </ProfileCard>

                    <ProfileCard title='Contact'>
                      <ProfileRow
                        label='Email'
                        value={
                          subscriber?.email ? (
                            <span className='break-all'>{subscriber.email}</span>
                          ) : null
                        }
                      />
                      <ProfileRow
                        label='Phone'
                        value={
                          subscriber?.homePhone || subscriber?.workPhone
                            ? formatPhone(subscriber?.homePhone ?? subscriber?.workPhone)
                            : null
                        }
                      />
                      <ProfileRow
                        label='Address'
                        value={
                          subscriber?.address.line1 ? (
                            <span>
                              {subscriber.address.line1}
                              {subscriber.address.line2 ? `, ${subscriber.address.line2}` : ''}
                              <br />
                              {[subscriber.address.city, subscriber.address.state]
                                .filter(Boolean)
                                .join(', ')}{' '}
                              {subscriber.address.zipCode ?? ''}
                            </span>
                          ) : null
                        }
                      />
                    </ProfileCard>

                    <ProfileCard title='Employment'>
                      <ProfileRow
                        label='Employee ID'
                        value={primaryMember.alternateId || null}
                        mono
                      />
                      <ProfileRow
                        label='Hire Date'
                        value={formatDate(
                          data.enrollments?.[0]?.dateEmployed ?? primaryMember.dateEnrolled,
                          'MMM D, YYYY',
                          '',
                        )}
                      />
                      <ProfileRow
                        label='Member Created'
                        value={formatDate(primaryMember.createdDate, 'MMM D, YYYY', '')}
                      />
                      <ProfileRow label='Status' value={getStatusBadge(primaryMember.isActive)} />
                      <ProfileRow
                        label='Opt-Out Status'
                        value={
                          primaryMember.plans.some((p) =>
                            (p.planId ?? '').toUpperCase().includes('OPT.OUT'),
                          ) ? (
                            <span
                              className='rounded bg-[#FEF3C7] px-1.5 py-0.5 text-[12px] font-semibold text-[#B45309]'
                              title="Plan Code OPT.Out.000 on the member's VBA profile"
                            >
                              Opted out
                            </span>
                          ) : (
                            <span className='text-muted-foreground'>Not opted out</span>
                          )
                        }
                      />
                      <ProfileRow
                        label='Opt-In Status'
                        value={<span className='text-muted-foreground'>Not tracked yet</span>}
                      />
                      <ProfileRow
                        label='Active Date'
                        value={formatDate(primaryMember.dateEnrolled, 'MMM D, YYYY', '')}
                      />
                      <ProfileRow
                        label='Inactive Date'
                        value={formatDate(
                          primaryMember.disenrollDate ?? data.enrollments?.[0]?.termDate,
                          'MMM D, YYYY',
                          '',
                        )}
                      />
                    </ProfileCard>

                    {/* Reference — effective window plus reference fields that
                        don't have a VBA source yet (render as em dashes). */}
                    <ProfileCard title='Reference'>
                      <ProfileRow
                        label='Effective Start'
                        value={formatDate(primaryMember.dateEnrolled, 'MMM D, YYYY', '')}
                      />
                      <ProfileRow
                        label='Effective End'
                        value={formatDate(
                          primaryMember.disenrollDate ?? data.enrollments?.[0]?.termDate,
                          'MMM D, YYYY',
                          '',
                        )}
                      />
                      <ProfileRow label='Open Enrollment' value={null} />
                      <ProfileRow label='Benefit Balance' value={null} />
                    </ProfileCard>
                  </>
                ) : (
                  <>
                    <Card className={cardClass}>
                      <CardHeader className='pb-2'>
                        <CardTitle className='text-xs font-semibold uppercase tracking-wide text-brand-gray'>
                          Personal Info
                        </CardTitle>
                      </CardHeader>
                      <CardContent className={isInternal ? 'text-sm' : 'space-y-2 text-sm'}>
                        {renderField(
                          'Name',
                          [
                            primaryMember.firstName,
                            // Demo parity: middle initial shows on internal.
                            isInternal && primaryMember.middleName
                              ? `${primaryMember.middleName.charAt(0)}.`
                              : null,
                            primaryMember.lastName,
                          ]
                            .filter(Boolean)
                            .join(' '),
                        )}
                        {renderField(
                          'Date of Birth',
                          primaryMember.birthDate
                            ? formatDate(primaryMember.birthDate, 'MMM D, YYYY')
                            : dash,
                        )}
                        {renderField(
                          'Age',
                          primaryMember.birthDate
                            ? String(
                                (() => {
                                  const birth = new Date(primaryMember.birthDate);
                                  const now = new Date();
                                  let age = now.getFullYear() - birth.getFullYear();
                                  if (
                                    now.getMonth() < birth.getMonth() ||
                                    (now.getMonth() === birth.getMonth() &&
                                      now.getDate() < birth.getDate())
                                  ) {
                                    age -= 1;
                                  }
                                  return age;
                                })(),
                              )
                            : dash,
                        )}
                        {renderField(
                          'Gender',
                          primaryMember.sex === 'M'
                            ? 'Male'
                            : primaryMember.sex === 'F'
                              ? 'Female'
                              : (primaryMember.sex ?? dash),
                        )}
                        {isInternal ? (
                          <InfoRow
                            label='SSN'
                            value={
                              <span className='flex items-center gap-2'>
                                {ssnRevealed
                                  ? formatSsn(primaryMember.ssn)
                                  : maskSsn(primaryMember.ssn)}
                                {primaryMember.ssn && (
                                  <button
                                    type='button'
                                    className='rounded p-0.5 text-primary hover:bg-primary/10'
                                    onClick={() => setSsnRevealed((v) => !v)}
                                    aria-label={ssnRevealed ? 'Hide SSN' : 'Show SSN'}
                                  >
                                    {ssnRevealed ? (
                                      <EyeOff className='h-3.5 w-3.5' />
                                    ) : (
                                      <Eye className='h-3.5 w-3.5' />
                                    )}
                                  </button>
                                )}
                              </span>
                            }
                          />
                        ) : (
                          <div>
                            <div className='text-xs font-semibold uppercase tracking-wider text-brand-gray mb-1'>
                              SSN
                            </div>
                            <div className='flex items-center gap-2 text-brand-navy font-medium'>
                              {ssnRevealed
                                ? formatSsn(primaryMember.ssn)
                                : maskSsn(primaryMember.ssn)}
                              {primaryMember.ssn && (
                                <button
                                  type='button'
                                  className='rounded p-0.5 text-brand-blue hover:bg-brand-blue/10'
                                  onClick={() => setSsnRevealed((v) => !v)}
                                  aria-label={ssnRevealed ? 'Hide SSN' : 'Show SSN'}
                                >
                                  {ssnRevealed ? (
                                    <EyeOff className='h-3.5 w-3.5' />
                                  ) : (
                                    <Eye className='h-3.5 w-3.5' />
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                        {renderField(
                          'Email',
                          isInternal && subscriber?.email ? (
                            <span className='break-all'>{subscriber.email}</span>
                          ) : (
                            (subscriber?.email ?? dash)
                          ),
                        )}
                        {renderField(
                          'Phone',
                          formatPhone(subscriber?.homePhone ?? subscriber?.workPhone),
                        )}

                        {/* Address folds into Personal Info rather than its own card. */}
                        <div className='mt-4 border-t border-gray-100 pt-4'>
                          <div className='text-xs font-semibold uppercase tracking-wide text-brand-gray mb-2'>
                            Address
                          </div>
                          <div className={isInternal ? '' : 'space-y-2'}>
                            {renderField('Street', subscriber?.address.line1 ?? dash)}
                            {subscriber?.address.line2 &&
                              renderField('Street 2', subscriber.address.line2)}
                            {renderField('City', subscriber?.address.city ?? dash)}
                            {renderField('State', subscriber?.address.state ?? dash)}
                            {renderField('ZIP', subscriber?.address.zipCode ?? dash)}
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className={cardClass}>
                      <CardHeader className='pb-2'>
                        <CardTitle className='text-xs font-semibold uppercase tracking-wide text-brand-gray'>
                          Employment
                        </CardTitle>
                      </CardHeader>
                      <CardContent className={isInternal ? 'text-sm' : 'space-y-2 text-sm'}>
                        {isInternal ? (
                          /* Internal users never get the client-admin inline edit,
                         so this is a plain demo-style row. */
                          <InfoRow label='Employee ID' value={primaryMember.alternateId || dash} />
                        ) : (
                          <div>
                            <div className='flex items-center gap-2'>
                              <div className='text-xs font-semibold uppercase tracking-wider text-brand-gray'>
                                Employee ID
                              </div>
                              {isClientAdmin && canEditMember && !externalIdEditing && (
                                <button
                                  type='button'
                                  onClick={startEditingExternalId}
                                  className='text-brand-blue hover:text-brand-blue-dark'
                                  aria-label='Edit external ID'
                                >
                                  <Pencil className='h-3 w-3' />
                                </button>
                              )}
                            </div>
                            {externalIdEditing ? (
                              <div className='mt-1 space-y-2'>
                                <Input
                                  value={externalIdValue}
                                  onChange={(e) => setExternalIdValue(e.target.value)}
                                  maxLength={32}
                                  placeholder='External ID'
                                  autoFocus
                                />
                                {externalIdError && (
                                  <p className='text-sm text-red-500'>{externalIdError}</p>
                                )}
                                <div className='flex items-center gap-2'>
                                  <Button
                                    size='sm'
                                    onClick={handleSaveExternalId}
                                    disabled={updateExternalIdMutation.isPending}
                                  >
                                    {updateExternalIdMutation.isPending ? (
                                      <Loader2 className='h-4 w-4 mr-1 animate-spin' />
                                    ) : (
                                      <Save className='h-4 w-4 mr-1' />
                                    )}
                                    Save
                                  </Button>
                                  <Button
                                    variant='outline'
                                    size='sm'
                                    onClick={() => {
                                      setExternalIdEditing(false);
                                      setExternalIdError(null);
                                    }}
                                    disabled={updateExternalIdMutation.isPending}
                                  >
                                    <X className='h-4 w-4 mr-1' />
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className='text-brand-navy font-medium'>
                                {primaryMember.alternateId || 'N/A'}
                              </div>
                            )}
                          </div>
                        )}
                        {renderField(
                          'Hire Date',
                          formatDate(
                            data.enrollments?.[0]?.dateEmployed ?? primaryMember.dateEnrolled,
                            'MMM D, YYYY',
                            dash,
                          ),
                        )}
                        {renderField(
                          'Member Created Date',
                          formatDate(primaryMember.createdDate, 'MMM D, YYYY', dash),
                        )}
                        {isInternal ? (
                          <InfoRow label='Status' value={getStatusBadge(primaryMember.isActive)} />
                        ) : (
                          <div>
                            <div className='text-xs font-semibold uppercase tracking-wider text-brand-gray mb-1'>
                              Status
                            </div>
                            <span className='flex flex-wrap items-center gap-2'>
                              {getStatusBadge(primaryMember.isActive)}
                              {gapCoverageBadge}
                            </span>
                          </div>
                        )}
                        {renderField(
                          'Opt-Out Status',
                          primaryMember.plans.some((p) =>
                            (p.planId ?? '').toUpperCase().includes('OPT.OUT'),
                          ) ? (
                            isInternal ? (
                              <span
                                className='rounded bg-[#FEF3C7] px-1.5 py-0.5 text-[12px] font-semibold text-[#B45309]'
                                title="Plan Code OPT.Out.000 on the member's VBA profile"
                              >
                                Opted out
                              </span>
                            ) : (
                              'Opted out'
                            )
                          ) : isInternal ? (
                            <span className='text-muted-foreground'>Not opted out</span>
                          ) : (
                            'Not opted out'
                          ),
                        )}
                        {renderField(
                          'Opt-In Status',
                          isInternal ? (
                            <span className='text-muted-foreground'>Not tracked yet</span>
                          ) : (
                            'Not tracked yet'
                          ),
                        )}
                        {renderField(
                          'Active Date',
                          formatDate(primaryMember.dateEnrolled, 'MMM D, YYYY', dash),
                        )}
                        {renderField(
                          'Inactive Date',
                          formatDate(
                            primaryMember.disenrollDate ?? data.enrollments?.[0]?.termDate,
                            'MMM D, YYYY',
                            '—',
                          ),
                        )}
                      </CardContent>
                    </Card>

                    {/* Coverage & Reference — effective window plus reference fields
                    that don't have a VBA source yet (shown as em dashes). */}
                    <Card className={cardClass}>
                      <CardHeader className='pb-2'>
                        <CardTitle className='text-xs font-semibold uppercase tracking-wide text-brand-gray'>
                          Coverage &amp; Reference
                        </CardTitle>
                      </CardHeader>
                      <CardContent className={isInternal ? 'text-sm' : 'space-y-2 text-sm'}>
                        {renderField(
                          'Effective Start',
                          formatDate(primaryMember.dateEnrolled, 'MMM D, YYYY', dash),
                        )}
                        {renderField(
                          'Effective End',
                          formatDate(
                            primaryMember.disenrollDate ?? data.enrollments?.[0]?.termDate,
                            'MMM D, YYYY',
                            '—',
                          ),
                        )}
                        {renderField('Open Enrollment', '—')}
                        {renderField('Benefit Balance', '—')}
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>

              {/* Internal renders the demo's full-bleed card (its own header row,
                  banner, and table edges); client keeps the padded card body. */}
              <Card className={isInternal ? `${cardClass} overflow-hidden` : cardClass}>
                <CardContent className={isInternal ? 'p-0' : 'p-5'}>
                  <MemberProductsTable
                    products={productRows}
                    unavailableMessage={
                      isExternalTpaMember
                        ? `Products not available for ${externalTpaLabel} members.`
                        : undefined
                    }
                  />
                </CardContent>
              </Card>

              <Card className={cardClass}>
                <CardHeader className='pb-2'>
                  <CardTitle className='flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-brand-gray'>
                    <CalendarRange className='h-3.5 w-3.5' />
                    Coverage Windows
                  </CardTitle>
                </CardHeader>
                <CardContent className='text-sm'>
                  {currentWindow ? (
                    renderCoverageWindow(currentWindow, true)
                  ) : (
                    <p className='py-1 text-xs text-brand-gray'>No current coverage window.</p>
                  )}
                  {pastWindows.length > 0 && (
                    <details className='group mt-2 border-t border-gray-100 pt-2'>
                      <summary className='flex cursor-pointer select-none list-none items-center gap-1 text-xs font-medium text-brand-blue'>
                        <span className='transition-transform group-open:rotate-90'>▸</span>
                        History ({pastWindows.length})
                      </summary>
                      {pastWindows.map((w) => renderCoverageWindow(w, false))}
                    </details>
                  )}
                </CardContent>
              </Card>

              <Card className={cardClass}>
                <CardContent className='p-5'>
                  <MemberDependentsSection
                    dependents={dependentRows}
                    canManage={canManageDependents}
                    onAddDependent={() => setAddDependentOpen(true)}
                    onDropDependent={(row) =>
                      setRemoveDependent({
                        memberSeq: row.memberSeq,
                        name:
                          [row.firstName, row.lastName].filter(Boolean).join(' ') ||
                          'this dependent',
                      })
                    }
                  />
                </CardContent>
              </Card>

              {/* Hidden for external-TPA members: notes resolve their tenant through the
                  member's VBA groups, which these members don't have, so the panel can
                  only error. Tracked separately — notes for them need their own context
                  loader keyed on the TPA group. */}
              {!isExternalTpaMember && (
                <MemberNotesPanel
                  memberId={composedMemberId}
                  legacyNotes={
                    primaryMember.notes?.trim()
                      ? [
                          {
                            id: 'member-notes',
                            text: primaryMember.notes,
                            author: 'VBA member record',
                          },
                        ]
                      : []
                  }
                />
              )}

              {/* External-TPA members always have an app-user source — their accounts
                  live in the same `hcues-user` collection, linked on the EDI id — so
                  the affiliation walk that gates the CBS path doesn't apply. */}
              {(isExternalTpaMember || (hasAppUserAffiliation && id)) && (
                <AppUserCard
                  subscriberId={id}
                  externalTpaMemberId={externalTpaMemberId}
                  hasAppUserAffiliation={isExternalTpaMember || hasAppUserAffiliation}
                  primaryIsActive={primaryMember.isActive}
                />
              )}
            </TabsContent>

            {/* Edit Member modal — replaces the old Edit Contact tab; the same
                form and save orchestration, launched from the header Edit action. */}
            {subscriber && (
              <Dialog
                open={subscriberEditing}
                onOpenChange={(open) => {
                  if (!open && !isSavingSubscriber) setSubscriberEditing(false);
                }}
              >
                <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-2xl'>
                  <DialogHeader>
                    <DialogTitle>Edit Member</DialogTitle>
                    <DialogDescription>
                      Update the member&apos;s identity, name, contact, and address information.
                    </DialogDescription>
                  </DialogHeader>
                  <div className='space-y-6'>
                    {/* Read-only fields */}
                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
                      <ReadOnlyField label='Subscriber ID' value={subscriber.subscriberId} />
                      <ReadOnlyField
                        label='External ID'
                        value={primaryMember.alternateId ?? 'N/A'}
                      />
                      <ReadOnlyField label='Relationship' value={primaryMember.relationshipLabel} />
                    </div>

                    {/* Editable identity fields (primary member) */}
                    <div className='pt-4 border-t'>
                      <h4 className='font-semibold text-brand-navy mb-4'>Identity</h4>
                      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                        <div className='space-y-2'>
                          <Label htmlFor='sub-ssn'>SSN</Label>
                          <Input
                            id='sub-ssn'
                            type='password'
                            autoComplete='off'
                            {...subscriberForm.register('ssn')}
                            maxLength={11}
                            placeholder='123-45-6789'
                          />
                          <p className='text-xs text-brand-gray'>
                            Leave empty to keep the existing SSN.
                          </p>
                          {subscriberForm.formState.errors.ssn && (
                            <p className='text-sm text-red-500'>
                              {subscriberForm.formState.errors.ssn.message}
                            </p>
                          )}
                        </div>
                        <div className='space-y-2'>
                          <Label htmlFor='sub-dob'>Date of Birth</Label>
                          <Input
                            id='sub-dob'
                            type='date'
                            {...subscriberForm.register('dateOfBirth')}
                          />
                          {subscriberForm.formState.errors.dateOfBirth && (
                            <p className='text-sm text-red-500'>
                              {subscriberForm.formState.errors.dateOfBirth.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Editable name fields */}
                    <div className='pt-4 border-t'>
                      <h4 className='font-semibold text-brand-navy mb-4'>Name</h4>
                      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                        <div className='space-y-2'>
                          <Label htmlFor='sub-first'>First Name</Label>
                          <Input
                            id='sub-first'
                            {...subscriberForm.register('firstName')}
                            maxLength={50}
                          />
                          {subscriberForm.formState.errors.firstName && (
                            <p className='text-sm text-red-500'>
                              {subscriberForm.formState.errors.firstName.message}
                            </p>
                          )}
                        </div>
                        <div className='space-y-2'>
                          <Label htmlFor='sub-last'>Last Name</Label>
                          <Input
                            id='sub-last'
                            {...subscriberForm.register('lastName')}
                            maxLength={50}
                          />
                          {subscriberForm.formState.errors.lastName && (
                            <p className='text-sm text-red-500'>
                              {subscriberForm.formState.errors.lastName.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Editable contact fields */}
                    <div className='pt-4 border-t'>
                      <h4 className='font-semibold text-brand-navy mb-4'>Contact Information</h4>
                      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                        <div className='space-y-2'>
                          <Label htmlFor='sub-email'>Email</Label>
                          <Input
                            id='sub-email'
                            type='email'
                            {...subscriberForm.register('email')}
                            maxLength={256}
                          />
                          {subscriberForm.formState.errors.email && (
                            <p className='text-sm text-red-500'>
                              {subscriberForm.formState.errors.email.message}
                            </p>
                          )}
                        </div>
                        <div className='space-y-2'>
                          <Label htmlFor='sub-home-phone'>Home Phone</Label>
                          <Input
                            id='sub-home-phone'
                            type='tel'
                            {...subscriberForm.register('homePhone')}
                            maxLength={20}
                          />
                          {subscriberForm.formState.errors.homePhone && (
                            <p className='text-sm text-red-500'>
                              {subscriberForm.formState.errors.homePhone.message}
                            </p>
                          )}
                        </div>
                        <div className='space-y-2'>
                          <Label htmlFor='sub-work-phone'>Work Phone</Label>
                          <Input
                            id='sub-work-phone'
                            type='tel'
                            {...subscriberForm.register('workPhone')}
                            maxLength={20}
                          />
                          {subscriberForm.formState.errors.workPhone && (
                            <p className='text-sm text-red-500'>
                              {subscriberForm.formState.errors.workPhone.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Editable address fields */}
                    <div className='pt-4 border-t'>
                      <h4 className='flex items-center gap-2 font-semibold text-brand-navy mb-4'>
                        <MapPin className='h-4 w-4 text-brand-blue' />
                        Address
                      </h4>
                      <div className='space-y-4'>
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                          <div className='space-y-2'>
                            <Label htmlFor='sub-addr1'>Address Line 1</Label>
                            <Input
                              id='sub-addr1'
                              {...subscriberForm.register('address')}
                              maxLength={128}
                            />
                          </div>
                          <div className='space-y-2'>
                            <Label htmlFor='sub-addr2'>Address Line 2</Label>
                            <Input
                              id='sub-addr2'
                              {...subscriberForm.register('address2')}
                              maxLength={128}
                            />
                          </div>
                        </div>
                        <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                          <div className='space-y-2'>
                            <Label htmlFor='sub-city'>City</Label>
                            <Input
                              id='sub-city'
                              {...subscriberForm.register('city')}
                              maxLength={32}
                            />
                          </div>
                          <div className='space-y-2'>
                            <Label htmlFor='sub-state'>State</Label>
                            <Input
                              id='sub-state'
                              {...subscriberForm.register('state')}
                              maxLength={2}
                            />
                            {subscriberForm.formState.errors.state && (
                              <p className='text-sm text-red-500'>
                                {subscriberForm.formState.errors.state.message}
                              </p>
                            )}
                          </div>
                          <div className='space-y-2'>
                            <Label htmlFor='sub-zip'>Zip Code</Label>
                            <Input
                              id='sub-zip'
                              {...subscriberForm.register('zipCode')}
                              maxLength={32}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant='outline'
                      onClick={() => setSubscriberEditing(false)}
                      disabled={isSavingSubscriber}
                    >
                      <X className='h-4 w-4 mr-1' />
                      Cancel
                    </Button>
                    <Button
                      onClick={subscriberForm.handleSubmit(handleSaveSubscriber)}
                      disabled={isSavingSubscriber}
                    >
                      {isSavingSubscriber ? (
                        <Loader2 className='h-4 w-4 mr-1 animate-spin' />
                      ) : (
                        <Save className='h-4 w-4 mr-1' />
                      )}
                      Save
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}

            {isInternal && (
              <TabsContent value='invoicing'>
                <MemberInvoicingTab subscriberId={id!} />
              </TabsContent>
            )}

            {isInternal && (
              <TabsContent value='history'>
                <EntityHistoryTab entityType='MEMBER' entityId={id!} matchPrefix />
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>

      {isInternal && lifecycleGroupId && (
        <>
          <TerminateMemberDialog
            open={lifecycleDialog === 'terminate'}
            onClose={closeLifecycleDialog}
            onSuccess={refetchDetails}
            groupId={lifecycleGroupId}
            memberId={composedMemberId}
            memberName={memberFullName}
          />
          <ReinstateMemberDialog
            open={lifecycleDialog === 'reinstate'}
            onClose={closeLifecycleDialog}
            onSuccess={refetchDetails}
            groupId={lifecycleGroupId}
            memberId={composedMemberId}
            memberName={memberFullName}
          />

          <ChangeTierDialog
            open={!!tierProduct}
            onClose={() => setTierProduct(null)}
            onSuccess={refetchDetails}
            groupId={lifecycleGroupId}
            memberId={
              tierProduct
                ? `${primaryMember.subscriberId}-${tierProduct.memberSeq}`
                : composedMemberId
            }
            productId={tierProduct?.planId ?? ''}
            productName={tierProduct?.name ?? ''}
            currentTier={
              tierProduct
                ? (data.members
                    .find((m) => m.memberSeq === tierProduct.memberSeq)
                    ?.plans.find((p) => p.planId === tierProduct.planId)?.tier ?? null)
                : null
            }
          />

          <AddDependentDialog
            open={addDependentOpen}
            onClose={() => setAddDependentOpen(false)}
            onSuccess={refetchDetails}
            groupId={lifecycleGroupId}
            memberId={composedMemberId}
            availableProducts={activeSubscriberProducts}
          />

          <TerminateMemberDialog
            open={!!removeDependent}
            onClose={() => setRemoveDependent(null)}
            onSuccess={refetchDetails}
            groupId={lifecycleGroupId}
            memberId={
              removeDependent
                ? `${primaryMember.subscriberId}-${removeDependent.memberSeq}`
                : composedMemberId
            }
            memberName={removeDependent?.name ?? ''}
            dependentOnly
          />
        </>
      )}
    </div>
  );
};
